import { useParams } from 'react-router-dom';

import { useAuth } from '../../../../../core/auth';
import { useAppDispatch, useAppSelector } from '../../../../../lib/store';
import { selectBatchId } from '../../../../supervisor-queue/state/selector';
import { useGetSupervisorWorkspaceQuery } from '../../../api/supervisorApiSlice';
import { useSupervisorMode } from '../../../context/SupervisorModeContext';
import { selectReviewTabSelected } from '../../../state/selector';
import { setReviewTabSelected } from '../../../state/supervisorSlice';

export interface UseSupervisorReviewTabSectionResult {
  reviewTabSelected: string;
  handleReviewTabChange: (value: string) => void;
  annotatorOneName: string | undefined;
  annotatorTwoName: string | undefined;
  conflictsCount: number | undefined;
  resolvedCount: number | undefined;
  isDataLoading: boolean;
}

export const useSupervisorReviewTabSection = (): UseSupervisorReviewTabSectionResult => {
  const dispatch = useAppDispatch();
  const { encounterId } = useParams();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  const batchId = useAppSelector(selectBatchId);
  const {
    data: supervisorWorkspace,
    isLoading: isSupervisorWorkspaceLoading,
    isFetching: isSupervisorWorkspaceFetching,
  } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );
  const annotators = supervisorWorkspace?.data.annotators;
  const annotatorOneName = annotators?.[0]?.annotatorName;
  const annotatorTwoName = annotators?.[1]?.annotatorName;
  const annotationsReview = supervisorWorkspace?.data.annotationsReview ?? [];

  const conflictsCount = annotationsReview.reduce((sum, item) => {
    const unresolved = item.conflicts.filter(
      (c) => (c.resolved?.annotations?.length ?? 0) === 0
    ).length;

    return sum + unresolved;
  }, 0);

  const resolvedCount = annotationsReview.reduce((sum, item) => {
    const resolvedAnnotationsCount = item.conflicts.reduce(
      (conflictSum, c) => conflictSum + (c.resolved?.annotations?.length ?? 0),
      0
    );

    return sum + item.matchingAnnotation.length + resolvedAnnotationsCount;
  }, 0);

  const reviewTabSelected = useAppSelector(selectReviewTabSelected);

  const handleReviewTabChange = (value: string) => {
    dispatch(setReviewTabSelected(value));
  };
  return {
    reviewTabSelected,
    handleReviewTabChange,
    annotatorOneName,
    annotatorTwoName,
    conflictsCount,
    resolvedCount,
    isDataLoading: isSupervisorWorkspaceFetching || isSupervisorWorkspaceLoading,
  };
};
